import { useParams, Link, Navigate } from 'react-router-dom';
import { getPost, getPosts, alternatesFor, blogBase, strings, SITE_URL } from '../lib/blog';
import { localePath, htmlLang, studioUrl, STUDIO_URL, type Lang } from '../lib/i18n';
import { BlogHeader } from '../components/BlogHeader';
import { Footer } from '../components/Footer';
import { Seo } from '../components/Seo';
import { CtaBlock } from '../components/CtaBlock';
import { ShareButtons } from '../components/ShareButtons';

export default function BlogPost({ lang = 'en' as Lang }: { lang?: Lang }) {
  const { slug } = useParams();
  const t = strings[lang];
  const base = blogBase(lang);
  const post = slug ? getPost(lang, slug) : undefined;
  if (!post) return <Navigate to={base} replace />;

  const url = `${SITE_URL}${base}/${post.slug}`;
  const image = post.cover.startsWith('http') ? post.cover : `${SITE_URL}${post.cover}`;

  // hreflang across all translated versions of this article.
  const alts = alternatesFor(post);
  const alternates = alts.map((p) => ({
    hreflang: htmlLang[p.lang],
    href: `${SITE_URL}${localePath(p.lang, `/blog/${p.slug}`)}`,
  }));
  const enAlt = alts.find((p) => p.lang === 'en');
  if (enAlt) {
    alternates.push({ hreflang: 'x-default', href: `${SITE_URL}${localePath('en', `/blog/${enAlt.slug}`)}` });
  }

  const articleLd = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    inLanguage: htmlLang[lang],
    headline: post.title,
    description: post.description,
    image,
    datePublished: post.date,
    dateModified: post.updated,
    author: { '@type': 'Organization', name: 'ArmProgress', url: SITE_URL },
    publisher: {
      '@type': 'Organization',
      name: 'MCDevLabs',
      url: STUDIO_URL,
      logo: { '@type': 'ImageObject', url: `${STUDIO_URL}/images/mcdevlabs-logo.png` },
    },
    mainEntityOfPage: url,
    keywords: post.keywords.join(', '),
  };

  const faqLd = post.faq.length
    ? {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: post.faq.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      }
    : null;

  const related = getPosts(lang)
    .filter((p) => p.slug !== post.slug)
    .slice(0, 2);

  return (
    <div className="min-h-screen bg-dark">
      <Seo
        title={`${post.title} — ArmProgress`}
        description={post.description}
        canonical={url}
        image={image}
        type="article"
        lang={lang}
        jsonLd={faqLd ? [articleLd, faqLd] : articleLd}
        alternates={alternates}
      />
      <BlogHeader lang={lang} />
      <article className="container mx-auto px-6 py-12 max-w-3xl">
        <Link to={base} className="btn-3d btn-3d--ghost btn-3d--sm px-4 py-2 text-sm">
          {t.allArticles}
        </Link>
        <h1 className="font-heading text-3xl md:text-5xl font-black text-ink tracking-tight mt-7 mb-4 leading-[1.1]">
          {post.title}
        </h1>
        <p className="inline-flex rounded-full bg-elevated px-3 py-1 text-faint text-sm font-semibold mb-10">
          {post.date} · {post.readingTime} {t.minRead} · {t.publishedBy}{' '}
          <a href={studioUrl(lang, '/blog#armprogress')} className="text-muted hover:text-[var(--brand-ink)] transition-colors">
            MCDevLabs
          </a>
        </p>
        <div className="prose-gm" dangerouslySetInnerHTML={{ __html: post.html }} />

        {post.faq.length > 0 && (
          <section className="mt-14">
            <h2 className="font-heading text-3xl font-black text-ink mb-5">{t.faqHeading}</h2>
            <div className="space-y-4">
              {post.faq.map((f, i) => (
                <div key={i} className="card-float p-5 md:p-6">
                  <h3 className="font-heading text-lg text-ink font-black mb-1.5">{f.q}</h3>
                  <p className="text-muted">{f.a}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        <ShareButtons url={url} title={post.title} lang={lang} />

        <CtaBlock lang={lang} />

        {related.length > 0 && (
          <section className="mt-12">
            <h2 className="font-heading text-2xl font-black text-ink mb-4">{t.keepReading}</h2>
            <ul className="grid gap-3 sm:grid-cols-2">
              {related.map((r) => (
                <li key={r.slug}>
                  <Link
                    to={`${base}/${r.slug}`}
                    className="card-chunky is-hoverable flex h-full items-center gap-3 p-4 font-heading font-black text-ink hover:text-[var(--brand-ink)]"
                  >
                    <span className="chip-icon w-9 h-9 flex-shrink-0" aria-hidden="true">→</span>
                    {r.title}
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}
      </article>
      <Footer lang={lang} />
    </div>
  );
}
